import * as uuid from 'uuid';

import { CalendarRequest } from './request';
import { CalendarStore } from './store';
import {
  CalendarCallType,
  CalendarEvents,
  CalendarDataParameters,
  CalendarManagerEvent,
  CalendarType,
  CalendarUpdateModeTable,
  DateRange,
  SetCalendarEvent,
  CalendarFeedId,
} from './entities';
import { WatchlistId, WatchlistManager } from '@benzinga/watchlist-manager';
import {
  EventsAction,
  getRequestParametersApi,
  getStartingParameters,
  NoRequestEventsActions,
  ParametersMap,
} from './generateManagerParams';
import { transform } from './transform';
import { omit, recalculateParameters } from './utils';
import { calendarDataParameters, CALENDAR_PAGESIZE } from './generateRestfulParams';
import { StockSymbol, UUID } from '@benzinga/session';
import { Session } from '@benzinga/session';
import { Subscribable } from '@benzinga/subscribable';
import { standardDates } from './standardDates';
import { AutocompleteItem, createSymbol } from '@benzinga/autocomplete-manager';
import { TickerSelectedEvent, WidgetLinkingManager } from '@benzinga/widget-linking';

export class CalendarFeed extends Subscribable<CalendarManagerEvent> {
  public request: CalendarRequest;
  private calendarType: CalendarType;
  private store: CalendarStore;
  private feedId: CalendarFeedId;
  private watchlistManager: WatchlistManager;
  private session: Session;

  constructor(session: Session, store: CalendarStore, calendarType: CalendarType, feedId: CalendarFeedId) {
    super();
    this.session = session;
    this.calendarType = calendarType;
    this.request = new CalendarRequest(session, feedId);
    this.feedId = feedId;
    this.store = store;
    this.watchlistManager = this.session.getManager(WatchlistManager);

    this.request.subscribe(event => {
      const currentSaga = this.store.getSaga(this.feedId);
      const prevEvents = this.store.getEvents(this.feedId);
      const { batchLoaded, transformedEvent } = transform(event, prevEvents, session, currentSaga);
      if (batchLoaded && currentSaga) {
        this.store.setSaga(this.feedId, currentSaga.id, batchLoaded);
      }
      if (transformedEvent) {
        this.store.storeParameters(transformedEvent, this.session);
        this.store.storeEvents(transformedEvent);
        this.setUpdateIntervalEventCall(transformedEvent);
        this.dispatch(transformedEvent);
      }
    });

    this.watchlistManager.subscribe(event => {
      const params = this.store.getParameters(this.feedId);
      let WLIds: string[];
      switch (event.type) {
        case 'watchlist:removed_watchlist':
          //filter out the removed watchlist and update if something was filtered out
          if (params) {
            WLIds = params.watchlistIds.filter(WLId => WLId !== event.watchlist.watchlistId);
            this.changeWatchlists(WLIds);
          }
          break;
        case 'watchlist:added_ticker_to_watchlist':
        case 'watchlist:removed_ticker_from_watchlist':
          if (params && params.watchlistIds.includes(event.watchlist.watchlistId)) {
            this.changeWatchlists(params.watchlistIds, true);
          }
          break;
      }
    });
  }

  get type(): CalendarType {
    return this.calendarType;
  }

  public loadCalendarEvents = (oldParametersMap: ParametersMap, newParameters: CalendarDataParameters): void => {
    const { events, parameters } = getStartingParameters(oldParametersMap, newParameters, this.session);
    this.store.setEvents(this.feedId, events);
    this.loadNewEvents(newParameters, events, parameters);
  };

  public stopUpdates = (clear?: boolean): void => {
    const updateInterval = this.store.getUpdateInterval(this.feedId);
    if (updateInterval) {
      clearInterval(updateInterval);
      // if clear is false it will set updateInterval to undefined but preserve intervalCallback
      this.store.setUpdateInterval(this.feedId, undefined, clear ? null : undefined);
    }
  };

  public restartUpdates = (): void => {
    this.stopUpdates();
    const intervalCallback = this.store.getIntervalCallback(this.feedId);
    let updateInterval = this.store.getUpdateInterval(this.feedId);
    if (!updateInterval && intervalCallback) {
      updateInterval = setInterval(intervalCallback, CalendarUpdateModeTable[this.calendarType]);
      this.store.setUpdateInterval(this.feedId, updateInterval);
    }
  };

  public updateCalendarEvents = (): void => {
    const prevEvents = this.getSafeEvents();
    const parameters = recalculateParameters(prevEvents, this.getSafeParameters());
    this.request.updateEvents(parameters);
  };

  public resetCalendar = async (): Promise<void> => {
    const parameters = this.getSafeParameters();
    const newParameters = { ...parameters, symbols: [], watchlistIds: [] };
    const parametersMap = this.store.generateParametersMap(this.calendarType);
    this.loadCalendarEvents(parametersMap, standardDates(newParameters));
  };

  public changeEvents(newParameters: CalendarDataParameters) {
    const parametersMap = this.store.generateParametersMap(this.calendarType);
    this.loadCalendarEvents(parametersMap, newParameters);
  }

  public changeFilters(filters: AutocompleteItem[]): void {
    const newParameters = { ...this.getSafeParameters(), filters };
    const parametersMap = this.store.generateParametersMap(this.calendarType);
    this.loadCalendarEvents(parametersMap, newParameters);
  }

  public changeDate = ({ dateEnd, dateStart }: DateRange): void => {
    const newParameters = { ...this.getSafeParameters(), dateEnd, dateStart };
    const parametersMap = this.store.generateParametersMap(this.calendarType);
    this.loadCalendarEvents(parametersMap, newParameters);
  };

  public changeWatchlists = (watchlistIds: WatchlistId[], force = false): void => {
    const newParameters = { ...this.getSafeParameters(), watchlistIds };
    const parametersMap = this.store.generateParametersMap(this.calendarType);
    if (force) {
      parametersMap.forEach((v, k) => {
        parametersMap.set(k, { ...v, parameters: { ...v.parameters, watchlistIds: [] } });
      });
    }
    this.loadCalendarEvents(parametersMap, newParameters);
  };

  public getSafeEvents = (): CalendarEvents[] => this.store.getEvents(this.feedId) || [];

  private getSafeParameters = (): CalendarDataParameters =>
    this.store.getParameters(this.feedId) || { ...calendarDataParameters };

  private setUpdateIntervalEventCall = (event: CalendarManagerEvent): void => {
    const { type } = event as SetCalendarEvent;
    if (type === CalendarCallType.SET_EVENTS) {
      this.setUpdateInterval();
    }
  };

  private setUpdateInterval = (): void => {
    this.stopUpdates(true);
    const intervalCallback = () => {
      if (!this.store.getIsLoading(this.feedId)) {
        this.updateCalendarEvents();
      }
    };
    const updateInterval = setInterval(intervalCallback, CalendarUpdateModeTable[this.calendarType]);

    this.store.setUpdateInterval(this.feedId, updateInterval, intervalCallback);
  };

  private loadNewEvents = (
    parameters: CalendarDataParameters | undefined,
    prevEvents: CalendarEvents[] | undefined,
    prevParameters: CalendarDataParameters | undefined = this.store.getParameters(this.feedId),
  ) => {
    const oldParameters: CalendarDataParameters | undefined =
      prevParameters && omit<CalendarDataParameters>(prevParameters, 'updated');
    const newParameters: CalendarDataParameters | undefined =
      parameters && omit<CalendarDataParameters>(parameters, 'updated');
    const sagaParameters = newParameters ?? oldParameters;
    if (sagaParameters) {
      const actions = getRequestParametersApi(prevEvents, oldParameters, newParameters, this.session);

      //This getting called with doNothing still updates sagaId, causing us to lose the events being fetched from backend
      if (actions.length === 1 && actions[0][0] === EventsAction.doNothing) {
        return;
      }

      const sagaId: UUID = uuid.v4();
      this.store.setSaga(this.feedId, sagaId);
      this.request.setParameters(sagaParameters, sagaId);

      const promises: Promise<void>[] = [];
      for (const action of actions) {
        const [eventsAction, params = sagaParameters, pagesize = CALENDAR_PAGESIZE] = action;
        const requestParameters = params
          ? {
              ...params,
              filters: autoCompleteSymbols(params.filters, this.session).map(s =>
                createSymbol({ name: s, symbol: s, type: 'symbol' }),
              ),
            }
          : undefined;
        if (NoRequestEventsActions.includes(eventsAction)) {
          this.request.amendEvents(requestParameters, params, prevEvents, eventsAction, sagaId);
        } else {
          promises.push(
            this.request.loadEvents(requestParameters, params, this.session, eventsAction, sagaId, pagesize),
          );
        }
      }
      if (promises.length) {
        Promise.all(promises);
      }
    }
  };
}

const autoCompleteSymbols = (autoComplete: AutocompleteItem[], session: Session): StockSymbol[] =>
  autoComplete.flatMap(a => {
    switch (a.type) {
      case 'symbol':
        return [a.tagId];
      case 'watchlist':
        return (
          session
            .getManager(WatchlistManager)
            .getStoredWatchlistById(a.tagId)
            ?.symbols.map(s => s.symbol) ?? []
        );
      case 'symbolLink': {
        const symbol = (
          session.getManager(WidgetLinkingManager).getWidgetLinkFeedByID(a.tagId)?.getLatestEvent('ticker_selected') as
            | TickerSelectedEvent
            | undefined
        )?.symbol;
        return symbol ? [symbol] : [];
      }
      default:
        return [];
    }
  });
